// components/categories/CategoryCard.tsx
'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowUpRight, Layers } from 'lucide-react';
import { Category } from '@/data/categories';
import { CornerBracket } from './CornerBracket';

// Per-category accent colours — keyed by id
const ACCENT_MAP: Record<string, string> = {
  brass: '#B08D57',
  copper: '#A0522D',
  wood: '#7B5E3A',
  marble: '#8E8E8E',
  aluminium: '#6B7A8C',
  'home-decor': '#8C7A6B',
  furniture: '#5C6B4A',
  lighting: '#C09B3A',
};

// Per-category tag labels
const TAG_MAP: Record<string, string> = {
  brass: 'Bestseller',
  copper: 'Premium',
  wood: 'Natural',
  marble: 'Luxury',
  aluminium: 'Industrial',
  'home-decor': 'Curated',
  furniture: 'Handcrafted',
  lighting: 'Trending',
};

interface CategoryCardProps {
  category: Category;
  index: number;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ category, index }) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null);
  const glintRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);

  // Individual bracket refs
  const bTL = useRef<HTMLSpanElement | null>(null);
  const bTR = useRef<HTMLSpanElement | null>(null);
  const bBL = useRef<HTMLSpanElement | null>(null);
  const bBR = useRef<HTMLSpanElement | null>(null);

  const quickLift = useRef<gsap.QuickToFunc | null>(null);
  const quickRotX = useRef<gsap.QuickToFunc | null>(null);
  const quickRotY = useRef<gsap.QuickToFunc | null>(null);
  const quickGlint = useRef<gsap.QuickToFunc | null>(null);

  const [hovered, setHovered] = useState(false);
  const accent = ACCENT_MAP[category.id] ?? '#B08D57';
  const tag = TAG_MAP[category.id] ?? 'Featured';
  const num = String(index + 1).padStart(2, '0');

  useGSAP(() => {
    if (!cardRef.current) return;

    gsap.set(cardRef.current, {
      transformPerspective: 900,
      transformOrigin: 'center center',
    });

    quickLift.current = gsap.quickTo(cardRef.current, 'y', { duration: 0.55, ease: 'power3.out' });
    quickRotX.current = gsap.quickTo(cardRef.current, 'rotationX', { duration: 0.65, ease: 'power3.out' });
    quickRotY.current = gsap.quickTo(cardRef.current, 'rotationY', { duration: 0.65, ease: 'power3.out' });

    if (glintRef.current) {
      gsap.set(glintRef.current, { x: -220 });
      quickGlint.current = gsap.quickTo(glintRef.current, 'x', { duration: 0.6, ease: 'power2.out' });
    }
  }, { scope: cardRef });

  const bracketRefs = () => [bTL.current, bTR.current, bBL.current, bBR.current];

  const handleMouseEnter = () => {
    setHovered(true);
    gsap.to(bracketRefs(), {
      opacity: 1,
      scale: 1,
      duration: 0.35,
      stagger: 0.05,
      ease: 'back.out(2)',
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;

    quickRotY.current?.(px * 9);
    quickRotX.current?.(-py * 9);
    quickLift.current?.(-8);
    quickGlint.current?.(
      ((e.clientX - rect.left) / rect.width) * (rect.width + 220) - 220
    );
  };

  const handleMouseLeave = () => {
    setHovered(false);
    quickRotY.current?.(0);
    quickRotX.current?.(0);
    quickLift.current?.(0);
    gsap.to(glintRef.current, { x: -220, duration: 0.4 });
    gsap.to(bracketRefs(), {
      opacity: 0,
      scale: 0.8,
      duration: 0.28,
      stagger: 0.04,
      ease: 'power2.in',
    });
  };

  // Origins for bracket transform
  const bracketOrigins: Record<string, string> = {
    tl: 'top left', tr: 'top right', bl: 'bottom left', br: 'bottom right',
  };

  return (
    <Link
      ref={cardRef}
      href={category.href ?? '/products'}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      aria-label={`Browse ${category.title} collection`}
      className="group relative block will-change-transform"
    >
      {/* Outer glow */}
      <div
        aria-hidden
        className="absolute -inset-px rounded-[8px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 50% 0%, ${accent}28 0%, transparent 68%)` }}
      />

      {/* Card shell */}
      <div className="relative bg-[#F8F5F0] rounded-[8px] overflow-hidden border border-[#1C1B19]/[0.08] shadow-[0_2px_8px_rgba(28,27,25,0.06)] group-hover:shadow-[0_28px_56px_-12px_rgba(28,27,25,0.22)] transition-shadow duration-500">

        {/* ── Decorative corner brackets ── */}
        {(
          [
            { corner: 'tl', ref: bTL },
            { corner: 'tr', ref: bTR },
            { corner: 'bl', ref: bBL },
            { corner: 'br', ref: bBR },
          ] as const
        ).map(({ corner, ref }) => {
          const isTop = corner === 'tl' || corner === 'tr';
          const isLeft = corner === 'tl' || corner === 'bl';
          return (
            <span
              key={corner}
              ref={ref}
              aria-hidden="true"
              style={{
                position: 'absolute',
                zIndex: 20,
                width: 14,
                height: 14,
                ...(isTop ? { top: 8 } : { bottom: 8 }),
                ...(isLeft ? { left: 8 } : { right: 8 }),
                borderTopWidth: isTop ? 1.5 : 0,
                borderBottomWidth: !isTop ? 1.5 : 0,
                borderLeftWidth: isLeft ? 1.5 : 0,
                borderRightWidth: !isLeft ? 1.5 : 0,
                borderStyle: 'solid',
                borderColor: accent,
                opacity: 0,
                transform: 'scale(0.8)',
                transformOrigin: bracketOrigins[corner],
              }}
            />
          );
        })}

        {/* ── Image ── */}
        <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#EDE9E0]">
          <Image
            src={category.image}
            alt={category.title}
            fill
            sizes="(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1280px) 33vw, 25vw"
            className="object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.1]"
            priority={index < 4}
          />

          {/* Glint sweep */}
          <div
            ref={glintRef}
            aria-hidden
            className="absolute inset-y-0 w-[80px] bg-gradient-to-r from-transparent via-white/18 to-transparent -skew-x-12 pointer-events-none z-10"
          />

          {/* Gradient veil */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent transition-opacity duration-500 group-hover:opacity-75" />

          {/* Index — top-left */}
          <div className="absolute top-3 left-3 z-10">
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/50 group-hover:text-white/80 transition-colors duration-300">
              {num}
            </span>
          </div>

          {/* Tag — top-right */}
          <div className="absolute top-3 right-3 z-10">
            <span
              className="inline-block px-2 py-[3px] text-[9px] tracking-[0.14em] uppercase font-semibold rounded-[3px]"
              style={{ background: `${accent}CC`, color: '#FEFDFB' }}
            >
              {tag}
            </span>
          </div>

          {/* Hover reveal panel */}
          <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] z-10">
            <div className="bg-white/10 backdrop-blur-[10px] border-t border-white/15 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers size={11} className="text-white/70" strokeWidth={1.5} />
                <span className="text-[9px] tracking-[0.18em] uppercase text-white/70 font-medium">
                  {category.subtitle}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] tracking-[0.16em] uppercase text-white font-semibold">
                  Explore
                </span>
                <ArrowUpRight size={13} className="text-white" strokeWidth={2} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Label ── */}
        <div ref={labelRef} className="relative bg-[#F8F5F0] px-4 py-[14px]">
          {/* Bottom colour wipe */}
          <div
            aria-hidden
            className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ background: `linear-gradient(to right, ${accent}, transparent)` }}
          />

          <span
            className="block text-[9px] tracking-[0.22em] uppercase font-semibold mb-[3px] transition-colors duration-300"
            style={{ color: hovered ? accent : '#9A9384' }}
          >
            {category.subtitle}
          </span>

          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-[15px] font-bold text-[#1C1B19] tracking-tight leading-snug">
              {category.title}
            </h3>
            <ArrowUpRight
              size={14}
              strokeWidth={1.5}
              className="shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-[#9A9384]"
            />
          </div>
        </div>
      </div>
    </Link>
  );
};